'use client';

import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Alert, Box, Typography } from '@mui/material';
import TradeIntro from '@/components/website/Trade/TradeIntro';
import TradeCard from '@/components/website/Trade/TradeCard';
import TradeCTA from '@/components/website/Trade/TradeCTA';
import { useMarketingSectionSpy } from '@/components/website/Marketing/marketing-scroll';
import { MARKETING } from '@/components/website/Marketing/marketing-tokens';
import MarketingPageSections, {
  renderMarketingSection,
} from '@/components/website/MarketingPage/MarketingPageSections';
import {
  indexEntriesFromSections,
  indexSpyIdsFromSections,
  resolvedImageAlt,
  resolvedImageUrl,
  TRADE_CMS_SLUG,
} from '@/components/website/MarketingPage/marketing-page-utils';
import WebsiteInteriorScrollLayout from './WebsiteInteriorScrollLayout';

const TRADE_SECTION_TYPES = new Set(['trade_intro', 'trade_card', 'trade_cta']);

function bodyToDescription(body) {
  if (!body) return [];
  if (Array.isArray(body)) return body.filter(Boolean);
  return String(body)
    .split(/\n\n+/)
    .map(part => part.trim())
    .filter(Boolean);
}

function heroScrollTargetId(sections) {
  const intro = (sections ?? []).find(section => section.type === 'intro_quote');
  if (intro?.id) return intro.id;
  const indexed = indexEntriesFromSections(sections);
  if (indexed[0]?.id) return indexed[0].id;
  const firstNonHero = (sections ?? []).find(section => section.type !== 'hero');
  return firstNonHero?.data?.indexId || firstNonHero?.id;
}

export function renderTradeSection(section, scrollTargetId) {
  const data = section?.data || {};

  switch (section.type) {
    case 'trade_intro':
      return (
        <TradeIntro
          key={section.id}
          id={section.id}
          title={data.title || ''}
          body={data.body || ''}
        />
      );
    case 'trade_card': {
      const aligned = data.imgAlign === 'right' ? 'right' : 'left';
      const imageUrl = resolvedImageUrl(data.imageAssetId);
      return (
        <TradeCard
          key={section.id}
          id={section.id}
          title={data.title || ''}
          description={bodyToDescription(data.body)}
          href={data.url || undefined}
          urlText={data.urlText || undefined}
          image={imageUrl || undefined}
          imageAlt={data.imageAlt || resolvedImageAlt(data.imageAssetId, data.title || '')}
          aligned={aligned}
        />
      );
    }
    case 'trade_cta':
      return (
        <TradeCTA
          key={section.id}
          id={section.id}
          title={data.title || ''}
          body={data.body || ''}
          imageAssetId={data.imageAssetId}
          imageAlt={data.imageAlt || ''}
          cta1Label={data.cta1Label || ''}
          cta1Url={data.cta1Url || ''}
          cta2Label={data.cta2Label || ''}
          cta2Url={data.cta2Url || ''}
        />
      );
    default:
      return renderMarketingSection(section, scrollTargetId);
  }
}

export default function TradePage({
  fontClassName,
  page: initialPage = null,
  preview = false,
  slug = TRADE_CMS_SLUG,
}) {
  const [page, setPage] = useState(initialPage);
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(preview);

  useEffect(() => {
    if (!preview) return undefined;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError('');
    axios
      .get(`/api/v2/gallery/marketing-pages/${encodeURIComponent(slug)}/preview/`, {
        withCredentials: true,
      })
      .then(({ data }) => {
        if (!cancelled) {
          setPage(data);
          setPreviewLoading(false);
        }
      })
      .catch(error => {
        if (cancelled) return;
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          setPreviewError('Draft preview requires a signed-in CMS editor.');
        } else if (status === 404) {
          setPreviewError('Draft preview is not available for this page.');
        } else {
          setPreviewError('Unable to load draft preview.');
        }
        setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [preview, slug]);

  const sections = page?.sections ?? [];
  const hasTradeSections = sections.some(section => TRADE_SECTION_TYPES.has(section?.type));
  const hasMarketingSections = sections.some(
    section => section?.type && !TRADE_SECTION_TYPES.has(section.type),
  );
  // Trade shares Brand/Process canvas + type so cards match the editorial theme.
  const useMarketingChrome = hasTradeSections || hasMarketingSections;
  const spyIds = useMemo(
    () => (hasTradeSections ? [] : indexSpyIdsFromSections(sections)),
    [hasTradeSections, sections],
  );
  const activeId = useMarketingSectionSpy(spyIds);
  const scrollTargetId = useMemo(() => heroScrollTargetId(sections), [sections]);

  const content = (() => {
    if (previewLoading) {
      return (
        <Typography sx={{ px: 3, py: 8 }} color="text.secondary">
          Loading draft preview…
        </Typography>
      );
    }
    if (previewError) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {previewError}
        </Alert>
      );
    }
    if (!page) {
      return <Typography sx={{ px: 3, py: 8 }}>Page not found.</Typography>;
    }
    // Brand/Process-only pages: full MarketingPageSections layout (sticky index, hero cue).
    if (!hasTradeSections) {
      return <MarketingPageSections sections={sections} activeId={activeId} />;
    }
    // Mixed or trade-only: preserve CMS order; Brand/Process types use shared renderers.
    return sections.map(section => renderTradeSection(section, scrollTargetId));
  })();

  return (
    <WebsiteInteriorScrollLayout
      contentSx={
        useMarketingChrome
          ? {
              bgcolor: MARKETING.canvas,
              color: MARKETING.text,
              p: 0,
            }
          : undefined
      }
    >
      <Box
        className={fontClassName}
        sx={
          useMarketingChrome
            ? {
                bgcolor: MARKETING.canvas,
                color: MARKETING.text,
                overflowX: 'clip',
              }
            : undefined
        }
      >
        {preview ? (
          <Alert
            severity="warning"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              borderRadius: 0,
            }}
          >
            Draft preview — unpublished changes. Not visible to the public.
          </Alert>
        ) : null}
        {content}
      </Box>
    </WebsiteInteriorScrollLayout>
  );
}

TradePage.propTypes = {
  fontClassName: PropTypes.string,
  page: PropTypes.object,
  preview: PropTypes.bool,
  slug: PropTypes.string,
};
